import { z } from "zod";
import { PAY_REPORT_NOTE_MAX_LENGTH } from "@goppy/domain";
import { memberDueStatusSchema, reportPaymentResponseSchema } from "./collections.js";
import { businessIdempotencyKeySchema } from "./ledger-correction.js";
import { evidenceKeySchema } from "./contribution-evidence.js";
import { recordPaidResponseSchema } from "./payments.js";

/* ===========================================================================
 * US-E6.F8-0784 (@UC0057 @UC0058 · QĐ người dùng 14/09/2026) — NỘP NHIỀU KỲ MỘT LẦN.
 *
 * Bối cảnh: đợt thu định kỳ đẻ ra MỘT `member_due` cho MỖI kỳ. Người còn thiếu 5 kỳ của cùng một đợt
 * thấy 5 dòng cùng tên trong sheet "Nộp quỹ" và không biết chọn dòng nào. Mobile gom theo đợt, cho
 * tick từng kỳ; nhưng ba việc dưới đây client KHÔNG được tự làm, nên contract này sinh ra:
 *
 *  1. **Tổng tiền của tập kỳ đã chọn** — client không cộng tiền (`apps/mobile/CLAUDE.md` §4) ⇒
 *     `quote` do server tính, từ CÙNG `outstandingMinorOf` mà mọi bề mặt khác dùng.
 *  2. **Ghi sổ nhiều khoản = MỘT transaction, một lần bấm** — không gọi `record-paid` N lần từ
 *     client: rớt mạng sau khoản thứ 2/3 là sổ ghi một nửa, người dùng bấm lại, và không ai biết
 *     nửa nào đã vào. Batch dùng lại ĐÚNG `recordPaidInTx`/`_reportInTx` (không engine thứ hai).
 *  3. **Nội dung chuyển khoản cho nhiều kỳ** — `GET …/bank-account?memberDueIds=` (ở `group-bank.ts`).
 *
 * Tiền trên dây là STRING số nguyên minor (B1). Mọi schema `.strict()`.
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/** Trần PG signed BIGINT — cùng hằng với `record-paid`/`report-payment`. */
const PG_BIGINT_MAX = 9223372036854775807n;

/**
 * Trần số khoản trong một lượt. Một người còn thiếu >50 kỳ của một nhóm là dữ liệu bất thường, không
 * phải ca dùng thật; trần để một body rác không kéo transaction dài vô hạn.
 */
export const MEMBER_DUES_BATCH_MAX_ITEMS = 50;

const positiveMinorString = (label: string) =>
  z
    .string()
    .trim()
    .regex(MONEY_INTEGER_STRING_PATTERN, `${label} phải là số nguyên (đơn vị nhỏ nhất)`)
    .refine((v) => {
      try {
        const n = BigInt(v);
        return n > 0n && n <= PG_BIGINT_MAX;
      } catch {
        return false;
      }
    }, `${label} phải lớn hơn 0 và không vượt giới hạn`);

/**
 * 🔴 Review PR #780 · M3 — `z.string().uuid()` nhận cả hex HOA, còn Postgres trả chữ THƯỜNG; server
 * ghép request↔row bằng `Map.get(id)` ⇒ id HOA hợp lệ mà không tìm thấy hàng ⇒ 500 do người dùng điều
 * khiển. Chuẩn hoá về chữ thường NGAY Ở BIÊN, và làm TRƯỚC phép kiểm trùng để `AAA…`/`aaa…` không
 * lọt thành hai phần tử.
 */
const memberDueIdSchema = z
  .string()
  .uuid()
  .transform((id) => id.toLowerCase());

const memberDueIdListSchema = z
  .array(memberDueIdSchema)
  .min(1, "Cần ít nhất một khoản")
  .max(MEMBER_DUES_BATCH_MAX_ITEMS)
  // Trùng id ⇒ 400 ở biên. Để lọt thì batch ghi một khoản hai lần trong cùng tx — khoá idempotency
  // của khoản đó chặn được, nhưng đó là chặn ở tầng cuối cùng cho một lỗi đáng ra chết ở tầng đầu.
  .refine((ids) => new Set(ids).size === ids.length, "Danh sách khoản có phần tử trùng");

/* ─── 1. QUOTE — POST /v1/groups/:id/members/:memberId/dues/quote ─────────────────────────────── */

export const memberDuesQuoteRequestSchema = z.object({ memberDueIds: memberDueIdListSchema }).strict();
export type MemberDuesQuoteRequest = z.infer<typeof memberDuesQuoteRequestSchema>;

/**
 * Một khoản trong báo giá. Ba con số đi CÙNG NHAU để client vẽ *"đã góp X/Y · còn Z"* mà không trừ:
 * `outstandingMinor` do server tính (`outstandingMinorOf`), KHÔNG phải `amountMinor − paidAmountMinor`
 * viết lại ở client (sai ở `awaiting_confirmation`, và JS không có int64).
 */
export const memberDuesQuoteItemSchema = z
  .object({
    memberDueId: z.string().uuid(),
    collectionId: z.string().uuid(),
    /** Tên đợt; `null` khi đợt không đặt tên — server KHÔNG bịa chuỗi thay thế. */
    collectionTitle: z.string().nullable(),
    /** Lịch định kỳ sinh ra kỳ này; `null` với đợt thu một lần. Client gom nhóm theo khoá này. */
    scheduleId: z.string().uuid().nullable(),
    /** Mã kỳ (`2026-09`, `2026-W37`…) và số thứ tự kỳ trong lịch — để dựng nhãn "Kỳ 3 · Tháng 9/2026". */
    periodKey: z.string().nullable(),
    periodIndex: z.number().int().positive().nullable(),
    status: memberDueStatusSchema,
    /** Số phải đóng của khoản (cả suất). */
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Số ĐÃ VÀO SỔ (trạng thái 3 — §3.7), KHÔNG tính lời khai đang chờ. */
    paidAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Phần CÒN THIẾU — `outstandingMinorOf`. Đây là số sẽ được điền vào ô "Số tiền". */
    outstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Hạn của đợt; `null` = không đặt hạn. Ngày lịch dạng ISO — client hiển thị NGÀY, không ghép giờ. */
    dueDate: z.string().datetime().nullable(),
    /** Cờ HIỂN THỊ do server derive — client không so `dueDate` với đồng hồ máy. */
    isOverdue: z.boolean(),
  })
  .strict();
export type MemberDuesQuoteItem = z.infer<typeof memberDuesQuoteItemSchema>;

/**
 * 🔴 Mọi khoản trong `memberDueIds` phải CÒN MỞ (pending / overdue / partially_paid /
 * awaiting_confirmation) và thuộc đúng `(groupId, memberId)`. Vi phạm ⇒ 409 `MEMBER_DUE_NOT_OPEN`
 * kèm `params.memberDueIds` = **chuỗi uuid phân cách dấu phẩy** (problem+json chỉ mang giá trị
 * nguyên thuỷ phẳng — `ProblemDetailFilter` loại mảng có chủ đích) — KHÔNG lặng lẽ bỏ qua rồi trả
 * tổng của phần còn lại: người dùng tưởng đang trả 3 kỳ mà tổng là của 2. Client tách chuỗi, bỏ tick
 * các khoản đó, báo *"Kỳ N không còn nộp được"* (trung tính — tập này gồm cả khoản được MIỄN, không nói "đã đóng"), rồi hỏi
 * lại. Khoản không thuộc (group, member) ⇒ 404.
 */
export const memberDuesQuoteResponseSchema = z
  .object({
    currency: z.string().length(3),
    items: z.array(memberDuesQuoteItemSchema).min(1),
    /** Σ `outstandingMinor` của `items` — server cộng, client hiển thị. */
    totalOutstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();
export type MemberDuesQuoteResponse = z.infer<typeof memberDuesQuoteResponseSchema>;

/**
 * Mã 409 "khoản không còn mở để ghi". Cửa LÔ (báo giá + ghi nhận lô): `params.memberDueIds` như docblock trên. Cửa MỘT KHOẢN
 * (`record-paid` · duyệt lời khai — RECORD-PAID-409-CODES, @UC0040 @UC0051) dùng CÙNG mã, `params.memberDueIds` = đúng một id
 * và thêm `params.status` = trạng thái hiện tại của khoản (đọc dưới khoá dòng) để client nói đúng lý do (`waived` = đã được miễn,
 * khác "đã đóng đủ"). Ngoại lệ: nhánh bất biến vỡ (luỹ kế đã bằng nghĩa vụ mà nhãn chưa khép) KHÔNG gửi `params.status` — nhãn
 * khi đó đang sai; client ra câu trung tính (DUE-CLOSED-REMOVED-COPY).
 */
export const MEMBER_DUE_NOT_OPEN_CODE = "MEMBER_DUE_NOT_OPEN" as const;

/* ─── 2. RECORD-PAID BATCH — POST /v1/member-dues/record-paid-batch (O/A/T ghi nhận hộ) ────────── */

export const recordPaidBatchItemSchema = z
  .object({
    memberDueId: memberDueIdSchema,
    /**
     * Số THỰC đóng cho khoản này. Vắng ⇒ đóng ĐỦ phần còn thiếu (cùng ngữ nghĩa `record-paid`).
     * Client mobile luôn gửi TƯỜNG MINH = `outstandingMinor` của báo giá, để số ghi sổ là số người
     * dùng vừa nhìn thấy, không phải số server tính lại ở một thời điểm khác.
     */
    paidAmountMinor: positiveMinorString("Số tiền thực đóng").optional(),
  })
  .strict();

export const recordPaidBatchRequestSchema = z
  .object({
    items: z
      .array(recordPaidBatchItemSchema)
      .min(1, "Cần ít nhất một khoản")
      .max(MEMBER_DUES_BATCH_MAX_ITEMS)
      .refine(
        (items) => new Set(items.map((i) => i.memberDueId)).size === items.length,
        "Danh sách khoản có phần tử trùng",
      ),
    /**
     * Khoá nghiệp vụ cho CẢ LƯỢT (tuỳ chọn, ổn định theo lần bấm). Từng khoản bên trong vẫn có khoá
     * riêng do `recordPaidInTx` derive ⇒ retry cả lượt sau khi đã commit ⇒ mọi khoản đều replay,
     * 0 bút toán mới — kể cả khi client cũ không gửi field này.
     */
    businessIdempotencyKey: businessIdempotencyKeySchema.optional(),
    /** 🔴 US-E6.F3-0910 (@UC0057) — khoá file bằng chứng (upload hai bước). KHÔNG đi vào khoá idempotency. */
    evidenceKey: evidenceKeySchema.optional(),
  })
  .strict();
export type RecordPaidBatchRequest = z.infer<typeof recordPaidBatchRequestSchema>;

/**
 * 🔴 RÀNG BUỘC PHẠM VI (server kiểm, 422 `BATCH_SCOPE_MIXED` khi vi phạm): mọi khoản phải thuộc CÙNG
 * một quỹ và CÙNG một suất (`groupMemberId`). Một lượt bấm là "người X nộp các kỳ của mình"; trộn hai
 * người hay hai quỹ vào một lượt là mở đường ghi nhầm chủ thể — bug đắt nhất repo (§3.6).
 * Vai: O/A/T của quỹ đó (cùng luật `record-paid`).
 */
export const recordPaidBatchResponseSchema = z
  .object({
    /** Cùng thứ tự với `items` của request. */
    results: z.array(recordPaidResponseSchema).min(1),
    /** Σ `amountMinor` đã ghi sổ trong lượt này — server cộng. */
    totalRecordedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
  })
  .strict();
export type RecordPaidBatchResponse = z.infer<typeof recordPaidBatchResponseSchema>;

export const BATCH_SCOPE_MIXED_CODE = "BATCH_SCOPE_MIXED" as const;

/* ─── 3. REPORT-PAYMENT BATCH — POST /v1/member-dues/confirmations/batch (member tự khai) ──────── */

export const reportPaymentBatchItemSchema = z
  .object({
    memberDueId: memberDueIdSchema,
    /** Số khai đã chuyển cho khoản này — BẮT BUỘC (mobile gửi `outstandingMinor` của báo giá). */
    paidAmountMinor: positiveMinorString("Số tiền thực đóng"),
  })
  .strict();

/**
 * Không có `proof` (ảnh chứng từ) ở đường batch: mobile không gửi ảnh ở sheet tự nộp, và một ảnh cho
 * N khoản là câu hỏi thiết kế riêng (chứng từ nào cho khoản nào). `note` dùng chung cho cả lượt.
 * Cờ `self_payment_auto_confirm` áp cho TỪNG khoản như đường đơn — response từng phần tử nói rõ
 * `status`/`newDueStatus` của khoản đó.
 */
export const reportPaymentBatchRequestSchema = z
  .object({
    items: z
      .array(reportPaymentBatchItemSchema)
      .min(1, "Cần ít nhất một khoản")
      .max(MEMBER_DUES_BATCH_MAX_ITEMS)
      .refine(
        (items) => new Set(items.map((i) => i.memberDueId)).size === items.length,
        "Danh sách khoản có phần tử trùng",
      ),
    note: z.string().trim().max(PAY_REPORT_NOTE_MAX_LENGTH).optional(),
    /**
     * Khoá nghiệp vụ cho CẢ LƯỢT (tuỳ chọn, ổn định theo lần bấm) — review PR #780 · M5. Có tác dụng ở
     * nhóm bật `self_payment_auto_confirm`: lời khai tự vào sổ, và khoá ledger dẫn xuất từ NỘI DUNG
     * (khoản + số + người) nên hai lần khai một-phần CÙNG số cho CÙNG khoản (100k rồi 100k nữa — hợp
     * lệ) sẽ đụng nhau ⇒ 409 giả. Gửi khoá theo lần bấm thì mỗi lượt là một bút toán; server thread
     * `${userId}:${key}:${memberDueId}` cho từng khoản — KHÔNG đưa nguyên khoá lô vào từng khoản.
     */
    businessIdempotencyKey: businessIdempotencyKeySchema.optional(),
    /** 🔴 US-E6.F3-0910 (@UC0057) — khoá file bằng chứng (upload hai bước). KHÔNG đi vào khoá idempotency. */
    evidenceKey: evidenceKeySchema.optional(),
  })
  .strict();
export type ReportPaymentBatchRequest = z.infer<typeof reportPaymentBatchRequestSchema>;

export const reportPaymentBatchResponseSchema = z
  .object({
    /** Cùng thứ tự với `items` của request. */
    results: z.array(reportPaymentResponseSchema).min(1),
    /** Σ `claimedAmountMinor` — server cộng. */
    totalClaimedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
  })
  .strict();
export type ReportPaymentBatchResponse = z.infer<typeof reportPaymentBatchResponseSchema>;
